import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDataGridTextFilter } from '../../../DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../../../DataGrid/DataGrid.jsx';
import { QuestionModal, Spinner } from '../../../index.jsx';
import { EventsListTableSettings } from './EventsTableSettings.jsx';
import { SeriousDeleteQuestion } from '../../../DeleteQuestion/SeriousDeleteQuestion.jsx';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useDataGridDateRangeFilter } from '../../../DataGrid/filters/useDataGridDateRangeFilter.jsx';

const EventsTable = ({
  events,
  onDelete,
  onDetailClick,
  onEditClick,
  onRowClick,
  onShowWorkshopsClick,
  onArchiveClick,
  isAdmin,
  isArchived,
}) => {
  const { t } = useTranslation();
  const nameTextFilterProps = useDataGridTextFilter('name');
  const dateFromDateRangeFilterProps = useDataGridDateRangeFilter('date_from');
  const dateToDateRangeFilterProps = useDataGridDateRangeFilter('date_from');

  const [rowToBeDeleted, setRowToBeDeleted] = useState(null);
  const user = useSelector((state) => state.auth.user);

  const columns = useMemo(
    () => [
      {
        title: t('Názov'),
        dataIndex: 'name',
        ellipsis: true,
        sorter: dataGridTextSorter('name'),
        defaultSortOrder: 'ascend',
        ...nameTextFilterProps,
      },
      {
        title: t('Začiatok akcie'),
        dataIndex: 'date_from',
        ellipsis: true,
        sorter: dataGridDateSorter('date_from'),
        render: (text) => (text ? dayjs(text).format('D.M.YYYY') : ''),
        defaultSortOrder: 'ascend',
        ...dateFromDateRangeFilterProps,
      },
      {
        title: t('Koniec akcie'),
        dataIndex: 'date_to',
        ellipsis: true,
        sorter: dataGridDateSorter('date_to'),
        render: (text) => (text ? dayjs(text).format('D.M.YYYY') : ''),
        defaultSortOrder: 'ascend',
        ...dateToDateRangeFilterProps,
      },
      {
        title: t('Počet seminárov'),
        dataIndex: 'workshops_count',
        ellipsis: true,
        sorter: (a, b) =>
          Number(a?.workshops_count ?? 0) - Number(b?.workshops_count ?? 0),
        render: (_, record) => Number(record?.workshops_count ?? 0),
      },
    ],
    [
      t,
      nameTextFilterProps,
      dateFromDateRangeFilterProps,
      dateToDateRangeFilterProps,
    ],
  );

  if (!events) {
    return <Spinner />;
  }

  return (
    <>
      <DataGrid
        rowKey={(event) => event.id}
        columns={columns}
        dataSource={events || []}
        onRowClick={onRowClick}
        openDeleteModal={false}
        customSettings={(row) => (
          <EventsListTableSettings
            event={row}
            onShowWorkshopsClick={onShowWorkshopsClick}
            onArchiveClick={onArchiveClick}
            isAdmin={isAdmin}
            isArchived={isArchived}
            onEditClick={onEditClick}
            onDetailClick={onDetailClick}
            onDelete={
              user && onDelete ? (row) => setRowToBeDeleted(row) : undefined
            }
          />
        )}
        optionsWidth={isAdmin ? 220 : 100}
      />
      <QuestionModal
        chosenPositive={() => {
          if (rowToBeDeleted) {
            onDelete(rowToBeDeleted);
            setRowToBeDeleted(null);
          }
        }}
        visible={rowToBeDeleted !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeleted(null)}
        headerTitle={t('Vymazať položku')}
        question={
          <SeriousDeleteQuestion
            firstText={t('Naozaj chcete vymazať túto akciu?')}
            mainNotice={t(
              'S akciou sa vymažú aj VŠETKY jeho semináre a VŠETKY termíny spolu s ich VŠETKÝMI hodnotami!',
            )}
            lastText={t(
              'Prosím uistite sa, že mažete správnu akciu, aby ste predišli nežiadúcim komplikáciam!',
            )}
          />
        }
      ></QuestionModal>
    </>
  );
};

EventsTable.propTypes = {
  events: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onRowClick: PropTypes.func,
  onShowWorkshopsClick: PropTypes.func,
  onArchiveClick: PropTypes.func,
  isAdmin: PropTypes.bool,
  isArchived: PropTypes.bool,
};

export default EventsTable;
